import { DependencyContainer } from './DependencyContainer';
import { IDIService } from "./IDIService";
import { CancelationTokenSource } from './CancelationTokenSource';
import { LocalStorageService } from "./Services/LocalStorageService";
import { ExalusConnectionService } from './Services/ExalusConnectionService';
import { WebApiCacheService } from "./Services/WebApi/WebApiCacheService";
import { UsersService } from "./Services/Users/UsersService";
import { DevicesService } from "./Services/Devices/DevicesService";
import { SessionService } from "./Services/Session/SessionService";
import { GeolocationService } from "./Services/Controller/GeolocationService";
import { ControllerConfigurationService } from "./Services/Controller/ControllerConfigurationService";
// 0. Import Module
import { initializeLinq, IEnumerable } from "linq-to-typescript"
import { ChannelsGroupsService } from "./Services/Devices/ChannelsGroupsService";
import { AppStateService } from "./Services/AppStateService";
import { PicturesService } from './Services/Pictures/PicturesService';
import { UpdatesService } from './Services/Updates/UpdatesService';
import { ManuallyPairedDevicesService } from './Services/Devices/ManuallyPairedDevicesService';
import { ControllerExtensionsService } from './Services/Controller/ControllerExtensionsService';
import { UpdatesProvider } from './Services/Updates/UpdatesProvider';
import { RemoteStorageService } from './Services/RemoteStorageService';
import { StatesHistoryService } from './Services/StatesHistory/StatesHistoryService';
import { ScenesService } from './Services/Scenes/ScenesService';
import { ChatService } from './Services/Chat/ChatService';
import { ConnectedAppsService } from './Services/Controller/ConnectedAppsService';
import { DataMigrationService } from './Services/DataMigrationService';
import { FindControllerService } from './Services/Controller/FindControllerService';
import { LocalNetworkExalusConnectionService } from './Services/LocalNetworkExalusConnectionService';
import { PictureInPictureService } from './Services/PictureInPicture';
import { ControllerNotificationsService } from './Services/Notifications/ControllerNotificationsService';
import { AndroidLocationService } from './Services/AndroidLocationService';
import { AndroidNotificationsService } from './Services/AndroidNotificationService';
import { AppRateService } from './Services/AppRateService';
import { AppLocalesService } from './Services/AppLocalesService';
import { AndroidAutoCarPlayService } from './Services/AndroidAutoCarPlayService';
import { HomeKitService } from './Services/HomeKitService';
import { ConfigBackupService } from './Services/Controller/ConfigBackupService';
// 1. Declare that the JS types implement the IEnumerable interface
declare global {
    interface Array<T> extends IEnumerable<T> { }
    interface Uint8Array extends IEnumerable<number> { }
    interface Uint8ClampedArray extends IEnumerable<number> { }
    interface Uint16Array extends IEnumerable<number> { }
    interface Uint32Array extends IEnumerable<number> { }
    interface Int8Array extends IEnumerable<number> { }
    interface Int16Array extends IEnumerable<number> { }
    interface Int32Array extends IEnumerable<number> { }
    interface Float32Array extends IEnumerable<number> { }
    interface Float64Array extends IEnumerable<number> { }
    interface Map<K, V> extends IEnumerable<[K, V]> { }
    interface Set<T> extends IEnumerable<T> { }
    interface String extends IEnumerable<string> { }

    namespace ExalusApi {
        function GetDependencyContainer(): DependencyContainer;
    }
}
// 2. Bind Linq Functions to Array, Map, etc
initializeLinq();

export function GetDependencyContainer(): DependencyContainer {
    return DependencyContainer.Instance;
}

export class Api {
    static WorksInContextOf: string = "";
    static Version: number = 1.0;

    static Init(): void {
        if (DependencyContainer.IsInitialized)
            return;
        
        console.log("Initializing DependencyContainer");
        let dep = DependencyContainer.Instance;

        try {
            dep.RegisterService(new DataMigrationService());
            dep.RegisterService(new LocalStorageService());
            dep.RegisterService(new AppStateService());
            dep.RegisterService(new WebApiCacheService());
            dep.RegisterService(new SessionService());
            dep.RegisterService(new PictureInPictureService());
            dep.RegisterService(new AppRateService());
            dep.RegisterService(new AppLocalesService());

            if (Api.IsRunningFromLocalNetwork()) {
                console.log("\n\nRunning from local network!\n\n");
                dep.RegisterService(new LocalNetworkExalusConnectionService());
            }
            else
                dep.RegisterService(new ExalusConnectionService());

            dep.RegisterService(new AndroidLocationService());
            dep.RegisterService(new AndroidAutoCarPlayService());
            dep.RegisterService(new AndroidNotificationsService());
            dep.RegisterService(new RemoteStorageService());
            dep.RegisterService(new GeolocationService());
            dep.RegisterService(new ControllerConfigurationService());
            dep.RegisterService(new ConfigBackupService());
            dep.RegisterService(new FindControllerService());
            dep.RegisterService(new ManuallyPairedDevicesService());
            dep.RegisterService(new ControllerExtensionsService());
            dep.RegisterService(new PicturesService());
            dep.RegisterService(new UsersService());
            dep.RegisterService(new DevicesService());
            dep.RegisterService(new ChannelsGroupsService());
            dep.RegisterService(new UpdatesProvider());
            dep.RegisterService(new UpdatesService());
            dep.RegisterService(new ConnectedAppsService());
            dep.RegisterService(new StatesHistoryService());
            dep.RegisterService(new ScenesService());
            dep.RegisterService(new ChatService());
            dep.RegisterService(new ControllerNotificationsService());
            dep.RegisterService(new HomeKitService());
        }
        catch (ex: any) {
            console.error(ex);
        }
    }

    static IsAppServedFromControllerAsync(): Promise<boolean> {
        // http reuqest to controller
        const url = `http://${window.location.hostname}/controller_info`;
        return fetch(url, { method: 'GET' })
            .then(response => {
                if (response.ok) {
                    return true;
                }
                return false;
            })
            .catch(error => {
                return false;
            });
    }

    static IsRunningFromLocalNetwork(hostname = window.location.hostname) {
        return (
            //(['localhost', '127.0.0.1', '', '::1'].includes(hostname))
            //|| (hostname.startsWith('192.168.'))
            (hostname.startsWith('192.168.'))
            || (hostname.startsWith('10.'))
            || (hostname.startsWith('172.16.'))
            //|| (hostname.endsWith('.local'))
        )
    }

    static GetAsync<T extends IDIService>(classType: string, cancelationToken?: CancelationTokenSource): Promise<T> {
        return DependencyContainer.Instance.GetServiceAsync<T>(classType, cancelationToken);
    }

    static GetWithTimeoutAsync<T extends IDIService>(classType: string, timeout: number): Promise<T> {
        return DependencyContainer.Instance.GetServiceWithTimeoutAsync<T>(classType, timeout);
    }

    static Get<T extends IDIService>(classType: string): T {
        return DependencyContainer.Instance.GetService<T>(classType);
    }

    static SleepForAsync(miliseconds: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, miliseconds));
    }

    static ExportStringAsFileToDownload(filename: string, content: string): HTMLAnchorElement {
        // Create a Blob from the string content
        const blob = new Blob([content], { type: 'text/plain' });

        // Create a link element
        const link = document.createElement('a');

        // Create a URL for the Blob and set it as the href attribute of the link
        const url = window.URL.createObjectURL(blob);
        link.href = url;

        // Set the download attribute with the desired filename
        link.download = filename;

        // Return the link element so the user can click it
        return link;
    }
}